/**
 * Shared Auth Utilities — Single Source of Truth
 * Mobile Agent Enhanced Version
 */

const STORAGE_KEY = 'omnigate_api_key';
const ADMIN_KEY_STORAGE = 'omnigate_admin_key';
const API_URL_STORAGE = 'omnigate_api_url';

// Keys that should NOT be sent as auth (placeholder/init values)
const INVALID_KEYS = new Set([
  'admin-initial-key',
  'admin-init-key',
  'nexus-admin-2026',
  '',
]);

// Default Telebit tunnel URL
export const DEFAULT_TELEBIT_URL = 'https://warm-mayfly-16.telebit.io';

/**
 * Get the active API base URL for mobile agent
 * Priority: 1) Saved URL, 2) Telebit (if Capacitor), 3) Local development
 */
export function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return DEFAULT_TELEBIT_URL;

  // Check for user-saved URL first
  const savedUrl = localStorage.getItem(API_URL_STORAGE);
  if (savedUrl) return savedUrl;

  // If running in Capacitor (Android/iOS app), use Telebit tunnel
  if ((window as any).Capacitor?.isNativePlatform?.()) {
    return DEFAULT_TELEBIT_URL;
  }

  // If accessed via Telebit or remote, use current origin
  if (window.location.hostname.includes('telebit.io')) {
    return window.location.origin;
  }

  // Development - use localhost backend
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8888';
  }

  // Production mode - use origin
  return window.location.origin;
}

/**
 * Check if backend is reachable (for offline mode detection)
 */
export async function checkBackendHealth(): Promise<{ online: boolean; latency?: number; error?: string }> {
  const baseUrl = getApiBaseUrl();
  const startTime = Date.now();

  try {
    const resp = await fetch(`${baseUrl}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    const latency = Date.now() - startTime;

    if (resp.ok) {
      return { online: true, latency };
    } else {
      return { online: false, error: `HTTP ${resp.status}` };
    }
  } catch (err: any) {
    return {
      online: false,
      error: err?.name === 'TimeoutError' ? 'Timeout' : 'Connection failed'
    };
  }
}

/**
 * Test Telebit tunnel connectivity
 */
export async function testTelebitConnection(): Promise<{ success: boolean; latency?: number; error?: string }> {
  const startTime = Date.now();

  try {
    const resp = await fetch(`${DEFAULT_TELEBIT_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(8000),
    });
    const latency = Date.now() - startTime;

    if (resp.ok) {
      return { success: true, latency };
    } else {
      return { success: false, error: `HTTP ${resp.status}` };
    }
  } catch (err: any) {
    return {
      success: false,
      error: err?.name === 'TimeoutError' ? 'Tunnel timeout' : 'Tunnel unreachable'
    };
  }
}

/**
 * Get current API key from localStorage
 */
export function getApiKey(): string {
  if (typeof window === 'undefined') return '';
  
  const key = localStorage.getItem(STORAGE_KEY) || '';
  
  // Debug for mobile app
  if (isMobileApp()) {
    console.log('[getApiKey] Mobile app detected');
    console.log('[getApiKey] Key length:', key.length);
    console.log('[getApiKey] Key is placeholder:', INVALID_KEYS.has(key));
    console.log('[getApiKey] All localStorage keys:', Object.keys(localStorage));
  }
  
  return key;
}

/**
 * Get admin key (fallback to regular key)
 */
export function getAdminKey(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(ADMIN_KEY_STORAGE) || getApiKey();
}

/**
 * Set API key in localStorage
 */
export function setApiKey(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, key);
}

/**
 * Set API URL in localStorage (for custom endpoints)
 */
export function setApiUrl(url: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(API_URL_STORAGE, url);
}

/**
 * Check if we have a valid (non-placeholder) API key
 */
export function hasValidKey(): boolean {
  const key = getApiKey();
  return key.length > 0 && !INVALID_KEYS.has(key);
}

/**
 * Build Authorization + X-API-Key headers from localStorage.
 */
export function authHeaders(): Record<string, string> {
  const key = getApiKey();
  if (!key || INVALID_KEYS.has(key)) {
    // Debug log for mobile app troubleshooting
    if (typeof window !== 'undefined' && isMobileApp()) {
      console.warn('[authHeaders] No valid API key found! Chat requests will fail.');
      console.log('[authHeaders] localStorage keys:', Object.keys(localStorage));
      console.log('[authHeaders] omnigate_api_key value:', localStorage.getItem('omnigate_api_key'));
    }
    return {};
  }
  return {
    'Authorization': `Bearer ${key}`,
    'X-API-Key': key,
  };
}

/**
 * Build admin auth headers
 */
export function adminAuthHeaders(): Record<string, string> {
  const key = getAdminKey();
  if (!key || INVALID_KEYS.has(key)) return {};
  return {
    'Authorization': `Bearer ${key}`,
    'X-API-Key': key,
  };
}

/**
 * Check if running as native mobile app (Capacitor)
 */
export function isMobileApp(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean((window as any).Capacitor?.isNativePlatform?.());
}

/**
 * Get connection status info for UI display
 */
export function getConnectionInfo(): {
  isMobile: boolean;
  apiEndpoint: string;
  hasValidKey: boolean;
  tunnelActive: boolean;
} {
  return {
    isMobile: isMobileApp(),
    apiEndpoint: getApiBaseUrl(),
    hasValidKey: hasValidKey(),
    tunnelActive: getApiBaseUrl().includes('telebit.io') || getApiBaseUrl() !== 'http://localhost:8888',
  };
}
